import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useScript } from "../../hooks/useScript";
import { LoadingSpinner } from "../digitalfriend/LoadingSpinner";
import { Message } from "../digitalfriend/Message";
import { ChooseAgent } from "../digitalfriend/Websocket/ChooseAgent";
import { applyConfiguration } from "../helpers/applyConfiguration";
import { useChatState, IMessage } from "../state/ChatState";

export const IFrameLayout = () => {
	const chatState = useChatState();
	const loaded = useScript({ src: "/scripts/testAgentScript.js" });
	const messages = chatState.chatMessages;
	const queryParams = new URLSearchParams(location.search);
	const sourceName = queryParams.get("sourceName") || "";
	const waveColor = queryParams.get("waveColor") || "#4A98D8";
	const [agentId, setAgentId] = useState<string>("");
	const prevAgentIdRef = useRef<string | null>(null);
	const [hasUserInteracted, setHasUserInteracted] = useState(false);

	const keyboardLabels = chatState.baseConfig?.KEYBOARD_LABELS?.split("|");
	const connectingMessages =
		chatState.baseConfig?.CONNECTING_MESSAGES?.split("|");

	const [initApproved, setInitApproved] = useState(false);
	const [isInitializing, setInitializing] = useState(true);
	const [languageIndex, setLanguageIndex] = useState(0);
	const [userMessage, setUserMessage] = useState<string>("");
	const [isChatEntryEnabled, setIsChatEntryEnabled] = useState(false);
	const [startedConversation, setStartedConversation] = useState(false);
	const [shouldFocusOnReady, setShouldFocusOnReady] = useState(false);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const stillBooting = !loaded || (!initApproved && isInitializing);

	const isVoice = queryParams.get("isVoice") === "true";
	const isChat = queryParams.has("isChat")
		? queryParams.get("isChat") === "true"
		: !isVoice;

	const [voiceAutoCloseEnabled, setVoiceAutoCloseEnabled] = useState(
		queryParams.get("voiceAutoCloseEnabled") === "true",
	);
	const [voiceAutoCloseTimeoutMs, setVoiceAutoCloseTimeoutMs] = useState(() => {
		const raw = parseInt(
			queryParams.get("voiceAutoCloseTimeoutSeconds") || "60",
			10,
		);
		return Math.min(Math.max(isNaN(raw) ? 60 : raw, 10), 300) * 1000;
	});

	const primaryColor =
		queryParams.get("primaryColor") ||
		chatState.baseConfig?.PRIMARY_COLOR ||
		"";

	const fontFamily = queryParams.get("fontFamily") || "";

	const bodyBackground = queryParams.get("bodyBackground") || "#FFFFFF";
	const assistantBubble = queryParams.get("assistantBubble") || "#F3F3F3";
	const assistantBubbleText =
		queryParams.get("assistantBubbleText") || "#0F172A";
	const userBubbleResolved =
		queryParams.get("userBubble") || primaryColor || "#1A0A91";
	const userBubbleTextParam = queryParams.get("userBubbleText");

	const inputPlaceholderFromUrl = queryParams.get("inputPlaceholder");
	const inputPlaceholder = useMemo(
		() =>
			inputPlaceholderFromUrl || keyboardLabels?.[languageIndex] || "Message",
		[inputPlaceholderFromUrl, keyboardLabels, languageIndex],
	);

	type StaticOption = { label: string; prompt: string };

	const staticOptionsRaw = queryParams.get("staticOptions");

	const showCopyrightClaim = queryParams.has("showCopyrightClaim")
		? queryParams.get("showCopyrightClaim") === "true"
		: true;

	const staticOptions: StaticOption[] = useMemo(() => {
		if (!staticOptionsRaw) return [];
		try {
			const parsed = JSON.parse(staticOptionsRaw) as StaticOption[] | unknown;
			if (!Array.isArray(parsed)) return [];
			return parsed
				.map((item) => {
					if (
						!item ||
						typeof item !== "object" ||
						typeof (item as StaticOption).label !== "string" ||
						typeof (item as StaticOption).prompt !== "string"
					) {
						return null;
					}
					return {
						label: (item as StaticOption).label.trim(),
						prompt: (item as StaticOption).prompt.trim(),
					};
				})
				.filter(
					(opt): opt is StaticOption => !!opt && !!opt.label && !!opt.prompt,
				)
				.slice(0, 4);
		} catch {
			return [];
		}
	}, [staticOptionsRaw]);

	useEffect(() => {
		if (loaded && isVoice && initApproved && canvasRef.current) {
			(
				window as Window & { symantoVizWaveColor?: string }
			).symantoVizWaveColor = waveColor;
			window.symantoAgentTester?.setupCanvas();
		}
	}, [loaded, isVoice, initApproved, waveColor]);

	useEffect(() => {
		const onWaveColorUpdate = (event: MessageEvent) => {
			if (event.data?.type !== "chat-widget-wave-color-update") return;
			if (typeof event.data?.waveColor !== "string") return;
			(
				window as Window & { symantoVizWaveColor?: string }
			).symantoVizWaveColor = event.data.waveColor;
		};
		window.addEventListener("message", onWaveColorUpdate);
		return () => window.removeEventListener("message", onWaveColorUpdate);
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const idFromURL = params.get("agentId");
		const fallbackId = chatState.baseConfig?.AGENT_IDS?.split("|")[0] || "";
		const resolvedAgentId = idFromURL || fallbackId;

		if (resolvedAgentId !== prevAgentIdRef.current) {
			prevAgentIdRef.current = resolvedAgentId;
			setAgentId(resolvedAgentId);

			setInitApproved(false);
			setInitializing(true);
		}
	}, [chatState.baseConfig, isVoice]);

	applyConfiguration(
		String(primaryColor),
		String(chatState.baseConfig?.SECONDARY_COLOR),
		String(chatState.baseConfig?.TITLE),
		bodyBackground || chatState.baseConfig?.BACKGROUND_COLOR || null,
	);

	const apiKey = chatState.baseConfig?.API_KEY;
	const baseWebSocketUrl = chatState.baseConfig?.BASE_API_URL;

	const startCall = (agentId: string, source?: string) => {
		chatState.resetState();

		window.symantoAgentTester.startConversation(
			agentId,
			chatState.baseConfig!.API_KEY,
			chatState.baseConfig!.BASE_API_URL,
			!isVoice,
			undefined,
			source,
		);

		window.symantoAgentTester.messageChannel.port2.onmessage = (event) => {
			const { id, role, isComplete, content } = event.data;
			const _msg: IMessage = {
				id: id,
				conversationId: id,
				messageAuthor: role == "2" ? 0 : 1,
				text: content,
				finalMessage: isComplete,
				date: new Date().toISOString(),
			};
			chatState.updateMessage(_msg, true);
		};

		window.symantoAgentTester.eventChannel.port2.onmessage = handleEventMessage;
	};

	const handleEventMessage = (event: { data: { type: any; message: any } }) => {
		const { type, message } = event.data;
		if (type === "success") {
			toast.dismiss();
			toast.success(message);
			setIsChatEntryEnabled(true);
		} else if (type === "error") {
			toast.error(message);
			setIsChatEntryEnabled(false);
		}
	};

	const initChat = async (
		langIndex: number,
		agentId: string,
		promptId?: string,
	) => {
		setInitializing(false);
		setInitApproved(true);
		setLanguageIndex(langIndex);

		const _agent = promptId && promptId !== "undefined" ? promptId : agentId;
		const source =
			sourceName?.trim() || document.referrer || window.location.host;

		setIsChatEntryEnabled(true);
		if (isChat) {
			startCall(_agent, source);
		}
	};

	const focusChatInput = () => {
		if (!isChat || isInitializing || !initApproved || !isChatEntryEnabled)
			return;
		const doFocus = () => {
			const el = inputRef.current;
			if (!el) return;
			el.focus({ preventScroll: true });

			if (el.value.length) {
				el.setSelectionRange(el.value.length, el.value.length);
			}
		};
		requestAnimationFrame(doFocus);
	};

	// Listen for the parent widget's focus handoff message
	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			if (event.data?.type !== "chat-widget-focus-input") return;

			if (isChat && initApproved && !isInitializing && isChatEntryEnabled) {
				focusChatInput();
			} else if (isChat) {
				setShouldFocusOnReady(true);
			}
		};
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [isChat, initApproved, isInitializing, isChatEntryEnabled]);

	useEffect(() => {
		if (
			shouldFocusOnReady &&
			isChat &&
			initApproved &&
			!isInitializing &&
			isChatEntryEnabled
		) {
			setShouldFocusOnReady(false);
			focusChatInput();
		}
	}, [
		shouldFocusOnReady,
		isChat,
		initApproved,
		isInitializing,
		isChatEntryEnabled,
	]);

	const sendMessage = (event: React.FormEvent) => {
		event.preventDefault();
		window.symantoAgentTester.sendTextMessage(userMessage);
		setUserMessage("");
		setHasUserInteracted(true);
	};

	const handleStaticOptionClick = (option: StaticOption) => {
		if (!option?.prompt) return;
		window.symantoAgentTester.sendTextMessage(option.prompt);
		setHasUserInteracted(true);
	};

	const selectDefaultAgent = () => {
		const promptId = chatState.baseConfig?.PROMPT_ID;
		initChat(0, agentId, promptId);
	};

	useEffect(() => {
		if (loaded && isInitializing && agentId) {
			selectDefaultAgent();
		}
	}, [loaded, isInitializing, agentId]);

	const startVoiceConversation = async () => {
		if (!agentId || !apiKey || !baseWebSocketUrl) return;

		try {
			await navigator.mediaDevices.getUserMedia({ audio: true });

			window.symantoAgentTester.startConversation(
				agentId,
				apiKey,
				baseWebSocketUrl,
				false,
				undefined,
				sourceName || window.location.host,
			);
			setStartedConversation(true);
		} catch (err) {
			console.error("Microphone access error:", err);
			toast.error(
				"Please allow microphone access to start voice conversation.",
			);
		}
	};

	const stopVoiceConversation = () => {
		window.symantoAgentTester.stopConversation();
		setStartedConversation(false);
	};

	useEffect(() => {
		const onStop = () => setStartedConversation(false);
		window.addEventListener("stopConversation", onStop);
		return () => window.removeEventListener("stopConversation", onStop);
	}, []);

	// Receive auto-close config pushed from the parent widget via postMessage.
	// This is the primary delivery mechanism when URL params are not available
	// (e.g. portal preview using the CDN version of chat-widget-unified.js).
	useEffect(() => {
		const onAutoCloseConfig = (event: MessageEvent) => {
			if (event.data?.type !== "chat-widget-auto-close-config") return;
			if (typeof event.data.voiceAutoCloseEnabled === "boolean") {
				setVoiceAutoCloseEnabled(event.data.voiceAutoCloseEnabled);
			}
			if (typeof event.data.voiceAutoCloseTimeoutSeconds === "number") {
				const clamped = Math.min(
					Math.max(event.data.voiceAutoCloseTimeoutSeconds, 10),
					300,
				);
				setVoiceAutoCloseTimeoutMs(clamped * 1000);
			}
		};
		window.addEventListener("message", onAutoCloseConfig);
		return () => window.removeEventListener("message", onAutoCloseConfig);
	}, []);

	// Auto-close inactive voice sessions
	useEffect(() => {
		if (!isVoice || !voiceAutoCloseEnabled || !startedConversation) return;

		const lastActivityRef = { current: Date.now() };

		const resetActivity = () => {
			lastActivityRef.current = Date.now();
		};

		const activityEvents = [
			"mousemove",
			"mousedown",
			"touchstart",
			"keydown",
			"pointerdown",
		];
		activityEvents.forEach((ev) =>
			window.addEventListener(ev, resetActivity, { passive: true }),
		);

		const interval = setInterval(() => {
			if (Date.now() - lastActivityRef.current >= voiceAutoCloseTimeoutMs) {
				stopVoiceConversation();
			}
		}, 5_000);

		return () => {
			clearInterval(interval);
			activityEvents.forEach((ev) =>
				window.removeEventListener(ev, resetActivity),
			);
		};
	}, [
		isVoice,
		voiceAutoCloseEnabled,
		startedConversation,
		voiceAutoCloseTimeoutMs,
	]);

	return (
		<div
			className="min-h-[100vh] max-h-[100vh] flex flex-col overflow-y-auto"
			style={{
				backgroundColor: bodyBackground,
				...(fontFamily ? { fontFamily } : {}),
			}}
		>
			{stillBooting && (
				<div className="flex flex-1 items-center justify-center">
					<LoadingSpinner />
				</div>
			)}

			{loaded && isChat && (
				<div
					className="flex flex-1 flex-col items-center overflow-y-auto mt-0 w-full"
					style={{ backgroundColor: bodyBackground }}
				>
					{initApproved ? (
						<div
							className="w-full max-w-[900px] flex flex-col flex-1 overflow-y-auto"
							style={{ backgroundColor: bodyBackground }}
						>
							{(isInitializing || messages.length === 0) && (
								<div className="flex w-full mt-1 md:mt-4 gap-3 items-center p-4">
									<p className="text-sm md:text-base">
										{connectingMessages?.[languageIndex]}
									</p>
								</div>
							)}

							{!isInitializing && (
								<>
									<div
										id="chat"
										className="flex flex-1 flex-col-reverse overflow-y-auto pt-3 px-1"
										style={{ backgroundColor: bodyBackground }}
									>
										{[...messages].reverse().map((message, index) => (
											<Message
												key={index}
												message={message}
												isIframe
												primaryColor={primaryColor}
												assistantBubbleBg={assistantBubble}
												assistantBubbleTextColor={assistantBubbleText}
												userBubbleBg={userBubbleResolved}
												userBubbleTextColor={userBubbleTextParam || undefined}
											/>
										))}
									</div>

									{staticOptions.length > 0 &&
										!hasUserInteracted &&
										messages.length > 0 && (
											<div className="flex w-full px-4 pt-2 pb-3 gap-2 flex-wrap">
												{staticOptions.map((option, index) => (
													<button
														key={`${option.label}-${index}`}
														type="button"
														onClick={() => handleStaticOptionClick(option)}
														title={option.label}
														className="inline-flex items-center justify-center rounded-full border text-xs md:text-sm hover:shadow-sm transition-colors h-9 px-3 bg-transparent max-w-[180px] md:max-w-[220px]"
														style={{
															borderColor: primaryColor || "#1A0A91",
															color: primaryColor || "#1A0A91",
														}}
													>
														<span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-center">
															{option.label}
														</span>
													</button>
												))}
											</div>
										)}

									<form
										onSubmit={sendMessage}
										className="flex w-full p-0 border-t-[1px] border-gray-custom"
										style={{
											borderTopColor: primaryColor || undefined,
										}}
									>
										<input
											ref={inputRef}
											type="text"
											placeholder={inputPlaceholder}
											disabled={!isChatEntryEnabled}
											value={userMessage}
											onChange={(e) => setUserMessage(e.target.value)}
											className="flex-1 px-4 py-3 bg-transparent text-base text-gray-900 placeholder-gray-custom focus:outline-none"
											required
										/>
										<button
											type="submit"
											disabled={!userMessage.length || !isChatEntryEnabled}
											className="px-4 py-2 disabled:opacity-30"
										>
											<svg
												className="w-6 h-6"
												fill={primaryColor}
												viewBox="0 0 1024 1024"
											>
												<path d="M85.76 896l895.573333-384-895.573333-384-0.426667 298.666667 640 85.333333-640 85.333333z" />
											</svg>
										</button>
									</form>
								</>
							)}
						</div>
					) : (
						<ChooseAgent selectedAgent={initChat} />
					)}
				</div>
			)}

			{loaded && isVoice && (
				<div className="flex flex-1 flex-col items-center overflow-y-auto bg-white mt-0">
					<div className="w-full max-w-[900px] flex flex-col flex-1 overflow-y-auto">
						{initApproved ? (
							<>
								{isInitializing && (
									<div className="flex w-full mt-4 gap-3 items-center justify-center p-4">
										<p className="text-sm md:text-base">
											{connectingMessages?.[languageIndex] || "Connecting..."}
										</p>
									</div>
								)}

								<div className="relative flex flex-1 items-center justify-center">
									<canvas
										ref={canvasRef}
										id="waveCallCanvas"
										width="300"
										height="300"
									/>

									{/* Play / Stop Button */}
									<div className="absolute inset-0 flex items-center justify-center">
										{!startedConversation ? (
											<button
												onClick={startVoiceConversation}
												className="w-16 h-16 rounded-full border border-gray-300 bg-[#f1f2f4] flex items-center justify-center shadow-sm hover:shadow-md transition"
												aria-label="Start"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													className="w-10 h-10 text-black"
												>
													<circle
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="2"
														fill="none"
													/>
													<polygon
														points="9,7 17,12 9,17"
														fill="currentColor"
													/>
												</svg>
											</button>
										) : (
											<button
												onClick={stopVoiceConversation}
												className="w-16 h-16 rounded-full border border-gray-300 bg-[#f1f2f4] flex items-center justify-center shadow-sm hover:shadow-md transition"
												aria-label="End Call"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													fill="currentColor"
													className="w-10 h-10 text-red-600"
												>
													<circle
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="2"
														fill="none"
													/>
													<rect
														x="9"
														y="9"
														width="6"
														height="6"
														rx="1"
														ry="1"
														fill="currentColor"
													/>
												</svg>
											</button>
										)}
									</div>
								</div>
							</>
						) : (
							<ChooseAgent selectedAgent={initChat} />
						)}
					</div>
				</div>
			)}

			{/* Single Footer shown only once */}
			<div className="pb-2 text-xs text-gray-custom text-center">
				{chatState.baseConfig?.FOOTER_IMAGE && (
					<div className="flex gap-2 align-middle justify-center items-center">
						Powered by{" "}
						<img
							src={chatState.baseConfig?.FOOTER_IMAGE}
							className="max-h-[26px] md:max-h-[60px]"
							alt="Logo"
						/>
					</div>
				)}

				{chatState.baseConfig?.FOOTER_IMAGE == undefined &&
					showCopyrightClaim && (
						<>
							Powered by{" "}
							<a
								href="https://www.symanto.com/ai-contact-centre"
								target="_blank"
								rel="noopener noreferrer"
								className="font-semibold hover:underline"
								style={{ color: primaryColor }}
							>
								Symanto
							</a>
						</>
					)}
			</div>
		</div>
	);
};
